import { assertUnreachable } from '.'

/**
 * - Is the string between 5 to 7 characters?
 * - Is the inward code first character numeric? (e.g. for 'AA9A 9AA', the
 *   inward code is '9AA')
 * - Are the last 2 characters non-numeric?
 *
 * @see https://ideal-postcodes.co.uk/guides/postcode-validation
 */
export const isValidPostcode = ({
  country,
  postcode,
}: {
  country: 'GB' | 'US'
  postcode: string
}) => {
  switch (country) {
    case 'GB': {
      return !!GbPostcodeRegExp.exec(postcode)
    }
    case 'US': {
      return !!UsPostcodeRegExp.exec(postcode)
    }
    default: {
      return assertUnreachable(country)
    }
  }
}

// Regex from Unicode CLDR: https://stackoverflow.com/a/7185241
export const UsPostcodeRegExp = /\d{5}([ \-]\d{4})?$/
export const GbPostcodeRegExp = /^[a-z]{1,2}\d[a-z\d]?\s*\d[a-z]{2}$/i

// Schemas

/**
 * GAIA ADDRESS
 *
 * Preferred address format
 */

type GaiaAddress = {
  line1: string
  line2?: string | null
  city?: string | null
  county?: string | null
  country: string
  postcode: string
}

type GaiaAddressFromDb = {
  line1: string | null
  line2?: string | null
  city?: string | null
  county?: string | null
  country: string | null
  postcode: string | null
}

export type RenderAddress = {
  street: string
  flat?: string | null
  houseName?: string | null
  houseNumber?: string | null
  district?: string | null
  city: string
  country: string
  postcode: string
}

export type AlloyAddress = {
  line1: string
  line2?: string | null
  city: string
  state: string | null
  countryCode: string
  postcode: string
}

// Transformers: to Gaia address

const transformRenderAddressToGaiaAddress = ({
  address,
}: {
  address?: RenderAddress
}): GaiaAddress => {
  return {
    line1: transformRenderAttributesToLine1({
      street: address?.street || '',
      flat: address?.flat,
      houseName: address?.houseName,
      houseNumber: address?.houseNumber,
    }),
    city: address?.city,
    county: address?.district,
    country: address?.country || '',
    postcode: address?.postcode || '',
  }
}

export const transformAlloyAddressToGaiaAddress = ({
  address,
}: {
  address?: AlloyAddress
}): GaiaAddress => {
  return {
    line1: address?.line1 || '',
    line2: address?.line2,
    city: address?.city,
    county: address?.state,
    country: address?.countryCode || '',
    postcode: address?.postcode || '',
  }
}

export const transformCreditAddressToGaiaAddress = ({
  country,
  address,
}:
  | {
      country: 'GB'
      address: RenderAddress
    }
  | {
      country: 'US'
      address: AlloyAddress
    }): GaiaAddress => {
  switch (country) {
    case 'GB': {
      return transformRenderAddressToGaiaAddress({ address })
    }
    case 'US': {
      return transformAlloyAddressToGaiaAddress({ address })
    }
    default: {
      return assertUnreachable(country)
    }
  }
}

// Transformers: to Alloy address

export const transformGaiaAddressToAlloyAdress = ({
  address,
}: {
  address?: Partial<GaiaAddressFromDb>
}): AlloyAddress => {
  return {
    line1: address?.line1 || '',
    line2: address?.line2,
    city: address?.city || '',
    state: address?.county || '',
    countryCode: address?.country || '',
    postcode: address?.postcode || '',
  }
}

// Transformers: to string

export const transformRenderAddressToString = ({
  street,
  flat,
  houseName,
  houseNumber,
  city,
  postcode,
}: {
  street: string
  flat?: string | null
  houseName?: string | null
  houseNumber?: string | null
  city?: string | null
  postcode?: string | null
}) => {
  const line1 = transformRenderAttributesToLine1({
    street,
    flat,
    houseName,
    houseNumber,
  })

  return [line1, city, postcode].filter((x) => !!x).join(', ')
}

export const transformRenderAttributesToLine1 = ({
  street,
  flat,
  houseName,
  houseNumber,
}: {
  street: string
  flat?: string | null
  houseName?: string | null
  houseNumber?: string | null
}) => {
  const flatLine = flat && `Flat ${flat}`
  const streetLine = [houseNumber, street].filter((x) => !!x).join(' ')

  return [flatLine, houseName, streetLine].filter((x) => !!x).join(', ')
}

export const transformAlloyAddressToString = ({
  line1,
  line2,
  city,
  state,
  postcode,
}: {
  line1: string
  line2?: string | null
  city: string
  state: string
  postcode: string
}) => {
  const formattedStatePostcode = [state, postcode].join(' ')

  return [line1, line2, city, formattedStatePostcode]
    .filter((x) => !!x)
    .join(', ')
}
